import Swall from 'sweetalert2';
import { HttpErrorResponse } from "@angular/common/http";
import { FormGroup } from "@angular/forms";
import { catchError, debounceTime, filter, MonoTypeOperatorFunction, Observable, ObservableInput, ObservedValueOf, OperatorFunction, pipe, tap, throwError } from "rxjs";
import { buildErrorDialogOptions } from './support-code.helpers';

interface ApiErrorWithMessage {
  message: string;
  statusCode?: number;
  type?: string;
  body?: any;
  code?: string;
  redirect?: string;
  validationErrors?: Record<string, string>;

}

export interface RestObject<T> {
  data: T;
  message: string;
  code: string;
  status: boolean;
}

export interface IPagedResponse<T> {
  content: T[];
  number: number;
  size: number;
  totalPages: number;
  totalElements: number;
  pageable?: {
    sort: {
      unsorted: boolean;
      sorted: boolean;
      empty: boolean;
    };
    offset: number;
    pageSize: number;
    pageNumber: number;
    paged: boolean;
    unpaged: boolean;
  };
}

export function isApiErrorWithMessage(err: any): err is ApiErrorWithMessage {
  return typeof err === 'object' && (err as Object).hasOwnProperty('message');
}

export function isHttpBadRequest(err: any): err is HttpErrorResponse {
  return err instanceof HttpErrorResponse && err.status === 400;
}

export function isHttpNotFound(err: any): err is HttpErrorResponse {
  return err instanceof HttpErrorResponse && err.status === 404;
}

export function isHttpUnauthorized(err: any): err is HttpErrorResponse {
  return err instanceof HttpErrorResponse && err.status === 401;
}

export function isHttpForbidden(err: any): err is HttpErrorResponse {
  return err instanceof HttpErrorResponse && err.status === 403;
}


/**
 * A frase que o backend mandou, quando mandou alguma.
 *
 * `detail` é o RFC 7807 da v3; `message` é o envelope da v1/v2. Devolve `null`
 * quando não há texto aproveitável — inclusive quando o corpo é uma string de
 * HTML (página de erro de proxy), que não se mostra para o produtor.
 */
export function mensagemDoBackend(err: unknown): string | null {
  const corpo = (err as { error?: unknown } | null)?.error;
  if (typeof corpo === 'string') {
    const limpo = corpo.trim();
    return limpo && !limpo.startsWith('<') && limpo.length <= 300 ? limpo : null;
  }
  const campos = corpo as { detail?: unknown; message?: unknown } | null;
  for (const valor of [campos?.detail, campos?.message]) {
    if (typeof valor === 'string' && valor.trim()) return valor.trim();
  }
  return null;
}

export function buildErrorMessage(error: ApiErrorWithMessage): string {
  let message = error.message;
  if (error.validationErrors) {
    message += '\n' + Object.entries(error.validationErrors).map(([key, value]) => `${key}: ${value}`).join('\n');
  }
  return message;
}

export function isHttpInternalServerError(err: any): err is HttpErrorResponse {
  return err instanceof HttpErrorResponse && err.status === 500;
}

export function isHttpConflict(err: any): err is HttpErrorResponse {
  return err instanceof HttpErrorResponse && err.status === 409;
}

type ErrorMessageCallback = (error: ApiErrorWithMessage) => string;
interface CatchRequestStatusErrorOptions {
  /**
   * Título da mensagem de erro
   */
  title?: string;
  /**
   * Título da mensagem de erro para recurso não encontrado
   */
  notFoundTitle?: string;
  /**
   * Título da mensagem de erro para ação não autorizada
   */
  forbiddenTitle?: string;
  /**
   * FormGroup para exibir os erros de validação com base no nome dos campos validados no back.
   */
  getForm?: () => FormGroup;
  /**
   * Título da mensagem de erro para erro desconhecido
   */
  unknownErrorTitle?: string;
  /**
   * builder para mensagem de erro customizada, recebe o erro completo e retorna a mensagem a ser exibida. Se fornecido, ignora os outros campos de título.
   */
  errorMessageBuilder?: ErrorMessageCallback;

}

/**
 *
 * @param {CatchRequestStatusErrorOptions} options - opções
 * @param {CatchRequestStatusErrorOptions['getForm']} getForm - FormGroup para exibir os erros de validação
 * @param {CatchRequestStatusErrorOptions['title']} title - Título da mensagem de erro
 * @param {CatchRequestStatusErrorOptions['notFoundTitle']} notFoundTitle - Título da mensagem de erro para recurso não encontrado
 * @param {CatchRequestStatusErrorOptions['forbiddenTitle']} forbiddenTitle - Título da mensagem de erro para ação não autorizada
 * @returns {OperatorFunction<T, T | ObservedValueOf<O>>} - Observable que emite o valor original ou o valor de erro
 */
export function catchRequestStatusError<T, O extends ObservableInput<any>>(options: CatchRequestStatusErrorOptions = {}): OperatorFunction<T, T | ObservedValueOf<O>> {
  const { getForm, title = 'Erro', notFoundTitle = 'Recurso não encontrado', forbiddenTitle = 'Ação não autorizada', unknownErrorTitle = 'Opa, tente novamente!', errorMessageBuilder=buildErrorMessage } = options || {};
  return catchError(err => {
    if (isHttpBadRequest(err) && isApiErrorWithMessage(err.error)) {
      const form = getForm?.();
      if (err.error.validationErrors) {
        Object.keys(err.error.validationErrors).forEach(key => {
          form?.controls[key]?.setErrors({

          })
        })
      }
      Swall.fire(buildErrorDialogOptions(err, { title: title ?? 'Erro', text: errorMessageBuilder(err.error) }))
      return throwError(() => err);
    }
    if (isHttpNotFound(err) && isApiErrorWithMessage(err.error)) {
      Swall.fire(buildErrorDialogOptions(err, { title: notFoundTitle ?? 'Recurso não encontrado', text: errorMessageBuilder(err.error) }))
      return throwError(() => err);
    }
    if (isHttpForbidden(err) && isApiErrorWithMessage(err.error)) {
      Swall.fire(buildErrorDialogOptions(err, { title: forbiddenTitle ?? 'Ação não autorizada', text: errorMessageBuilder(err.error) }))
      return throwError(() => err);
    }
    if (isHttpConflict(err) && isApiErrorWithMessage(err.error)) {
      Swall.fire(buildErrorDialogOptions(err, { title: unknownErrorTitle ?? 'Opa, parece que houve um conflito!', text: errorMessageBuilder(err.error) }))
      return throwError(() => err);
    }
    if (isHttpInternalServerError(err) && isApiErrorWithMessage(err.error)) {
      Swall.fire(buildErrorDialogOptions(err, { title: unknownErrorTitle ?? 'Opa, tente novamente!', text: errorMessageBuilder(err.error) }))
      return throwError(() => err);
    }

    // Nenhum ramo acima casou. Isso acontece mais do que parece: todos exigem
    // um `message` no corpo, então erro sem `message`, status fora da lista
    // (401, 502...) e falha de rede saíam DAQUI EM SILÊNCIO. Como o loader é um
    // Swall e só some quando outro diálogo o substitui, a tela ficava presa no
    // loader sem dizer nada — foi assim que o convite duplicado travou a tela.
    //
    // O texto sai do corpo quando o backend mandou algo (`detail` do
    // ProblemDetail na v3, `message` na v1/v2), e só cai no genérico quando não
    // há nada — "tente de novo" no lugar de "lote sem período configurado" é
    // conselho que não resolve.
    Swall.fire(
      buildErrorDialogOptions(err, {
        title: unknownErrorTitle ?? 'Opa, tente novamente!',
        text: mensagemDoBackend(err) ?? 'Não foi possível concluir a operação. Tente de novo.',
      }),
    );
    return throwError(() => err);
  })
}

export { catchRequestStatusError as withErrorHandlingMessage };


// Cache storage
export const requestCache = new Map<string, any>();
export const requestsOnGoing = new Set<string>();
export const defaultValidateFn = (e: any) => true;
// 1 minuto default
const DEFAULT_TTL = 1 * 60_000;
/**
 * Creates an RxJS operator that caches HTTP responses
 * @param keyFn Function to generate cache key
 * @param ttl Time to live in milliseconds (optional)
 */
export function sessionCacheRequest<T>(
  keyFn: () => string,
  ttl = DEFAULT_TTL,
  validateFn = defaultValidateFn
) {
  return (source: Observable<T>) => {
    return new Observable<T>(subscriber => {
      // Generate key based on input data
      const key = keyFn();

      // Check if data exists in cache and is not expired
      const cachedData = requestCache.get(key);
      if (cachedData) {
        console.debug('sessionCacheRequest hit:', key);
        // Return cached data without making a request
        subscriber.next(cachedData as T);
        subscriber.complete();
        return () => {};
      }

      const shouldCache = validateFn || defaultValidateFn;
      // Data not in cache, subscribe to original observable
      requestsOnGoing.add(key);

      const commonPipeOperator: MonoTypeOperatorFunction<T> = tap(response => {
        if (response && shouldCache(response)) {
          requestCache.set(key, response);
        } else {
          requestCache.delete(key);
        }

        // Set expiration
        if (ttl > 0) {
          setTimeout(() => {
            requestCache.delete(key);
          }, ttl);
        }
      });

      const extraPipes: [MonoTypeOperatorFunction<T>, MonoTypeOperatorFunction<T>] =
        requestsOnGoing.has(key)
          ? [debounceTime(50), commonPipeOperator]
          : [filter(_ => true), commonPipeOperator];
      const subscription = source.pipe(...extraPipes).subscribe({
        next: value => subscriber.next(value),
        error: err => {
          requestCache.delete(key);
          return subscriber.error(err);
        },
        complete: () => {
          requestsOnGoing.delete(key);
          return subscriber.complete();
        },
      });

      // Return teardown logic
      return () => {
        subscription.unsubscribe();
      };
    });
  };
}

export function invalidateCache<T>(keyFn: () => string) {
  return (source: Observable<T>) => {
    return new Observable<T>(subscriber => {
      // Generate key based on input data
      const key = keyFn();

      if (requestsOnGoing.has(key)) {
        return () => {};
      }

      requestsOnGoing.add(key);
      const subscription = source
        .pipe(
          tap(response => {
            console.debug('Cache invalidate:', key);
            requestCache.delete(key);
          })
        )
        .subscribe({
          next: value => subscriber.next(value),
          error: err => subscriber.error(err),
          complete: () => subscriber.complete(),
        });

      return () => {
        subscription.unsubscribe();
        requestsOnGoing.delete(key);
      };
    });
  };
}
